import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useScope } from "@/context/ScopeContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import TeacherExecutiveTab from "@/components/TeacherExecutiveTab";
import TeacherQualityTab from "@/components/TeacherQualityTab";
import TeacherDeploymentTab from "@/components/TeacherDeploymentTab";
import TeacherDrilldownTab from "@/components/TeacherDrilldownTab";
import TeacherInsightsTab from "@/components/TeacherInsightsTab";
import "@/components/TeacherExecutiveTab.css";
import "@/components/TeacherQualityTab.css";
import "@/components/TeacherDeploymentTab.css";
import "@/components/TeacherDrilldownTab.css";
import "@/components/TeacherInsightsTab.css";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  Users, 
  GraduationCap,
  AlertTriangle, 
  TrendingUp,
  TrendingDown,
  RefreshCw,
  Upload,
  School,
  Target,
  BarChart3,
  Activity,
  Award,
  Monitor,
  UserCheck,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  Brain
} from "lucide-react";
import { toast } from "sonner";
import ExportPanel from "@/components/ExportPanel";
import DashboardPageHeader from "@/components/DashboardPageHeader";
import { BlockLink, SchoolLink } from "@/components/DrilldownLink";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
  Legend,
  ComposedChart,
  Line,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis
} from "recharts";

import { getBackendUrl } from "@/lib/backend";

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const TAB_ITEMS = [
  { id: "executive", label: "Executive Overview", icon: BarChart3 },
  { id: "quality", label: "Quality & Training", icon: Award },
  { id: "deployment", label: "Deployment & Risk", icon: Activity },
  { id: "drilldown", label: "School Drilldown", icon: Target },
  { id: "insights", label: "Insights", icon: Brain },
];

// KPI Card Component
const KPICard = ({ label, value, suffix = "", icon: Icon, trend, trendValue, color = "blue", size = "default", description }) => {
  const colors = {
    blue: "bg-blue-50 text-blue-600",
    green: "bg-emerald-50 text-emerald-600",
    red: "bg-red-50 text-red-600",
    amber: "bg-amber-50 text-amber-600",
    purple: "bg-purple-50 text-purple-600",
    slate: "bg-slate-50 text-slate-600",
    cyan: "bg-cyan-50 text-cyan-600",
  };

  return (
    <Card className="border-slate-200" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className={size === "large" ? "p-6" : "p-4"}>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</p>
            <div className="flex items-baseline gap-1 mt-1">
              <span className={`${size === "large" ? "text-3xl" : "text-2xl"} font-bold text-slate-900 tabular-nums`}>
                {typeof value === 'number' ? value.toLocaleString('en-IN') : value}
              </span>
              {suffix && <span className="text-lg text-slate-500">{suffix}</span>}
            </div>
            {trend !== undefined && trendValue !== undefined && (
              <div className={`flex items-center gap-1 mt-1 text-sm ${trend === 'up' ? 'text-emerald-600' : trend === 'down' ? 'text-red-600' : 'text-slate-500'}`}>
                {trend === 'up' ? <TrendingUp className="w-3 h-3" /> : trend === 'down' ? <TrendingDown className="w-3 h-3" /> : <Minus className="w-3 h-3" />}
                <span>{trendValue}</span>
              </div>
            )}
            {description && <p className="text-xs text-slate-400 mt-1">{description}</p>}
          </div>
          {Icon && (
            <div className={`p-2 rounded-lg ${colors[color]}`}>
              <Icon className="w-5 h-5" strokeWidth={1.5} />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Gauge Component
const GaugeBar = ({ value, label, color = "#10b981", maxValue = 100 }) => {
  const percentage = Math.min((value / maxValue) * 100, 100);
  
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm text-slate-600">{label}</span>
        <span className="text-sm font-bold" style={{ color }}>{value}%</span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div 
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${percentage}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
};

const TeacherDashboard = () => {
  const { scope } = useScope();
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [overview, setOverview] = useState(null);
  const [blockData, setBlockData] = useState([]);
  const [schoolDistribution, setSchoolDistribution] = useState([]);
  const [topGains, setTopGains] = useState([]);
  const [topLosses, setTopLosses] = useState([]);
  const [trainingCoverage, setTrainingCoverage] = useState([]);
  const [qualificationRisk, setQualificationRisk] = useState([]);
  const [deploymentRisk, setDeploymentRisk] = useState([]);
  const [blockComparison, setBlockComparison] = useState([]);
  const [activeTab, setActiveTab] = useState("executive");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [
        overviewRes,
        blockRes,
        distributionRes,
        gainsRes,
        lossesRes,
        trainingRes,
        qualRes,
        deployRes,
        comparisonRes
      ] = await Promise.all([
        axios.get(`${API}/teacher/overview`),
        axios.get(`${API}/teacher/block-wise`),
        axios.get(`${API}/teacher/school-distribution`),
        axios.get(`${API}/teacher/top-changes?change_type=gain`),
        axios.get(`${API}/teacher/top-changes?change_type=loss`),
        axios.get(`${API}/teacher/training-coverage`),
        axios.get(`${API}/teacher/qualification-risk`),
        axios.get(`${API}/teacher/deployment-risk`),
        axios.get(`${API}/teacher/block-comparison`)
      ]);
      
      setOverview(overviewRes.data);
      setBlockData(blockRes.data);
      setSchoolDistribution(distributionRes.data);
      setTopGains(gainsRes.data);
      setTopLosses(lossesRes.data);
      setTrainingCoverage(trainingRes.data);
      setQualificationRisk(qualRes.data);
      setDeploymentRisk(deployRes.data);
      setBlockComparison(comparisonRes.data);
    } catch (error) {
      console.error("Error fetching Teacher data:", error);
      toast.error("Failed to load teacher data");
    } finally {
      setLoading(false);
    }
  }, [scope.version]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleImport = async () => {
    const url = "https://customer-assets.emergentagent.com/job_7d6cbc1e-b567-4fbc-af84-06fa5107bbd4/artifacts/85yv9qfh_2.%20MAHARASHTRA_School_Wise_Comparison_AY_2025-26.xlsx";
    
    setImporting(true);
    try {
      await axios.post(`${API}/teacher/import?url=${encodeURIComponent(url)}`);
      toast.success("Teacher data import started!");
      setTimeout(() => {
        fetchData();
        setImporting(false);
      }, 5000);
    } catch (error) {
      toast.error("Import failed: " + (error.response?.data?.detail || error.message));
      setImporting(false);
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-slate-900 text-white p-3 rounded-lg shadow-lg">
          <p className="font-medium">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {typeof entry.value === 'number' ? entry.value.toLocaleString() : entry.value}
              {entry.unit || ''}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="loading-spinner" />
      </div>
    );
  }

  const hasData = overview && overview.teachers_cy > 0;

  const headerCopy = {
    executive: {
      eyebrow: `Executive overview · School-wise teacher comparison · AY 2025–26`,
      subtitle:
        "Redesigned to surface teacher workforce health first, then diagnostics and actions. The layout uses clearer hierarchy, fewer competing cards, and more chart breathing space.",
      icon: BarChart3,
    },
    quality: {
      eyebrow: "Teacher quality lens · AY 2025–26 · Maharashtra",
      subtitle:
        "Quality and training readiness view for CTET qualification, CWSN preparedness, computer training coverage and below-graduation qualification risk.",
      icon: Award,
    },
    deployment: {
      eyebrow: "Teacher deployment · AY 2025–26 · Maharashtra",
      subtitle:
        "School-wise teacher deployment monitoring with deputation load, cross-school teaching, block risk exposure and staffing capacity signals.",
      icon: Activity,
    },
    drilldown: {
      eyebrow: "School-wise teacher drilldown · AY 2025–26 · Maharashtra",
      subtitle:
        "Identify schools with sudden staffing gains or losses, compare block-level teacher quality, and move quickly from school lists to accountable actions.",
      icon: Target,
    },
    insights: {
      eyebrow: "School-wise teacher comparison · AY 2025–26 · Maharashtra",
      subtitle:
        "Mistral-powered teacher intelligence briefing with priority signals, root-cause diagnostics, and actionable intervention queue.",
      icon: Brain,
    },
  };

  const copy = headerCopy[activeTab] || headerCopy.executive;
  const EyebrowIcon = copy.icon;

  return (
    <div className="teacher-dashboard space-y-6 animate-fade-in" data-testid="teacher-dashboard">
      <DashboardPageHeader
        title="Teacher Analytics Dashboard"
        subtitle={
          hasData
            ? copy.subtitle
            : "School-wise Teacher Comparison • AY 2025-26 • Maharashtra"
        }
        eyebrow={
          hasData ? (
            <>
              <EyebrowIcon size={14} />
              {copy.eyebrow}
            </>
          ) : null
        }
        actions={
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={handleImport}
              disabled={importing}
              data-testid="import-teacher-btn"
            >
              {importing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              {importing ? "Importing..." : "Import Data"}
            </Button>
            <ExportPanel dashboardName="teacher" dashboardTitle="Teacher Analytics" />
            <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </>
        }
      />

      {!hasData ? (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <GraduationCap className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No Teacher Data Available</h3>
            <p className="text-slate-500 mb-4">Click "Import Data" to load the Teacher Comparison Excel file</p>
            <Button onClick={handleImport} disabled={importing} data-testid="import-teacher-empty-btn">
              {importing ? "Importing..." : "Import Teacher Data"}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Dashboard Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <div className="tex-tabs-wrap" aria-label="Teacher dashboard sections">
              <nav className="tex-tabs">
                {TAB_ITEMS.map(({ id, label, icon: Icon }) => (
                  <button
                    key={id}
                    type="button"
                    className={`tex-tab ${activeTab === id ? "active" : ""}`}
                    onClick={() => setActiveTab(id)}
                    data-testid={`tab-${id}`}
                  >
                    <Icon />
                    {label}
                  </button>
                ))}
              </nav>
            </div>

            <TabsContent value="executive" className="mt-0">
              <TeacherExecutiveTab
                overview={overview}
                schoolDistribution={schoolDistribution}
                blockComparison={blockComparison}
                blockData={blockData}
                onNavigateTab={setActiveTab}
              />
            </TabsContent>

            <TabsContent value="quality" className="mt-0">
              <TeacherQualityTab
                overview={overview}
                trainingCoverage={trainingCoverage}
                qualificationRisk={qualificationRisk}
                blockData={blockData}
              />
            </TabsContent>

            <TabsContent value="deployment" className="mt-0">
              <TeacherDeploymentTab
                overview={overview}
                deploymentRisk={deploymentRisk}
                blockData={blockData}
              />
            </TabsContent>

            <TabsContent value="drilldown" className="mt-0">
              <TeacherDrilldownTab
                overview={overview}
                topGains={topGains}
                topLosses={topLosses}
                blockData={blockData}
                schoolDistribution={schoolDistribution}
              />
            </TabsContent>
            <TabsContent value="insights" className="mt-0">
              <TeacherInsightsTab overview={overview} />
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
};

export default TeacherDashboard;
